import { useState } from "react";
import type { LayoutDoc } from "../../shared/layout";
import { Icon, type IconName } from "../ui/Icon";
import { Sheet } from "../ui/Sheet";

// Screens shown directly in the phone's bottom bar; everything else is under "More".
const PRIMARY = ["family-home", "full-calendar", "chores", "kitchen"];

/**
 * Phone navigation: a four-button bottom bar plus "More" for every other
 * screen, pinning, editing, settings and the admin page.
 */
export function PhoneNav({
  layouts,
  activeId,
  own,
  ownOpen,
  editing,
  pinned,
  showPin,
  icons,
  onPin,
  onSelect,
  onEdit,
  onSettings,
}: {
  layouts: LayoutDoc[];
  activeId: string | undefined;
  /** Screens that aren't layouts (Today, the Living Atlas, Resorts…) that are on, listed under More; Today comes first. */
  own: { id: string; name: string; icon: IconName; onOpen: () => void }[];
  /** One of those is showing (it has no layout to edit). */
  ownOpen: boolean;
  editing: boolean;
  pinned: boolean;
  showPin: boolean;
  icons: Record<string, IconName>;
  onPin: () => void;
  onSelect: (id: string) => void;
  onEdit: () => void;
  onSettings: () => void;
}) {
  const [more, setMore] = useState(false);
  const primary = PRIMARY.map((k) => layouts.find((l) => l.presetKey === k)).filter((l): l is LayoutDoc => Boolean(l));
  const tabs = primary.length >= 3 ? primary.slice(0, 4) : layouts.slice(0, 4);
  const activeInMore = activeId !== undefined && !tabs.some((l) => l.id === activeId);
  const icon = (l: LayoutDoc) => icons[l.presetKey ?? ""] ?? "layout";
  const go = (fn: () => void) => () => {
    setMore(false);
    fn();
  };
  const ownButton = (o: (typeof own)[number]) => (
    <button key={o.id} className={`phone-screen ${activeId === o.id ? "on" : ""}`} onClick={go(o.onOpen)} disabled={editing}>
      <Icon name={o.icon} size="1.6rem" />
      <span>{o.name}</span>
    </button>
  );

  return (
    <>
      <nav className="phone-nav" aria-label="Screens">
        {tabs.map((l) => (
          <button key={l.id} className={`phone-tab ${l.id === activeId ? "on" : ""}`} onClick={() => onSelect(l.id)} disabled={editing && l.id !== activeId} aria-current={l.id === activeId ? "page" : undefined}>
            <Icon name={icon(l)} size="1.4rem" />
            <span>{l.name}</span>
          </button>
        ))}
        <button className={`phone-tab ${activeInMore ? "on" : ""}`} onClick={() => setMore(true)} aria-haspopup="dialog">
          <Icon name="grip" size="1.4rem" />
          <span>More</span>
        </button>
      </nav>
      {more && (
        <Sheet title="Screens" onClose={() => setMore(false)}>
          <div className="phone-screens">
            {own.filter((o) => o.id === "today").map(ownButton)}
            {layouts.map((l) => (
              <button key={l.id} className={`phone-screen ${l.id === activeId ? "on" : ""}`} onClick={go(() => onSelect(l.id))} disabled={editing && l.id !== activeId}>
                <Icon name={icon(l)} size="1.6rem" />
                <span>{l.name}</span>
              </button>
            ))}
            {own.filter((o) => o.id !== "today").map(ownButton)}
          </div>
          <div className="phone-actions">
            {showPin && (
              <button className="btn btn-block" onClick={go(onPin)}>
                <Icon name={pinned ? "lock" : "unlock"} size="1.2rem" /> {pinned ? "Unpin this screen" : "Pin this screen"}
              </button>
            )}
            <button className="btn btn-block" onClick={go(onEdit)} disabled={editing || ownOpen}>
              <Icon name="layout" size="1.2rem" /> Edit this screen
            </button>
            <button className="btn btn-block" onClick={go(onSettings)}>
              <Icon name="gear" size="1.2rem" /> Settings
            </button>
            <a className="btn btn-block" href="/admin">
              <Icon name="home" size="1.2rem" /> Admin page
            </a>
          </div>
        </Sheet>
      )}
    </>
  );
}
